import { useEffect, useState } from "react";
import { Building2, Cctv, Info, SlidersHorizontal, type LucideIcon } from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { screens } from "../screens";
import type { SettingsResponse } from "../../shared/api";
import { auditActorText, formatAuditTime } from "../../shared/audit";
import { AUTHORIZATION_LABEL, TOLERANCE_LABEL, type Settings } from "../../shared/settings";
import { formatNumber, formatPct } from "../../shared/versions";
import { CardSkeleton } from "@/components/Loading";
import { ErrorAlert } from "@/components/ErrorAlert";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { errorText, getSettings } from "@/lib/client";
import { navigate } from "@/lib/nav";

const HEAD = "sticky top-0 z-10 bg-card shadow-[inset_0_-1px_0_var(--border)]";

export function toleranceText(key: keyof Settings["tolerance"], v: number): string {
  const { unit } = TOLERANCE_LABEL[key];
  return unit === "%" ? formatPct(v) : `${formatNumber(v)} ${unit}`;
}

function CardHead({ id, title, icon: Icon }: { id: string; title: string; icon: LucideIcon }) {
  return (
    <div className="flex items-center gap-3">
      <span aria-hidden="true" className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary"><Icon className="size-4" /></span>
      <h2 id={id} className="text-lg font-semibold">{title}</h2>
    </div>
  );
}

function Dl({ id, title, icon, rows }: { id: string; title: string; icon: LucideIcon; rows: [string, React.ReactNode, string?][] }) {
  return (
    <Card role="region" aria-labelledby={id} className="gap-3 text-sm">
      <CardHead id={id} title={title} icon={icon} />
      <dl className="grid gap-x-4 gap-y-1 sm:grid-cols-[max-content_1fr]">
        {rows.map(([k, v, cls]) => <div key={k} className="contents"><dt className="text-muted-foreground">{k}</dt><dd className={cls}>{v}</dd></div>)}
      </dl>
    </Card>
  );
}

/** E10 Ayarlar (ui.md §4.7, ADR-0017): salt okunur, son ayar sürümü. */
export function SettingsScreen() {
  const [s, setS] = useState<SettingsResponse | null>(null);
  const [error, setError] = useState("");
  useEffect(() => {
    let live = true;
    getSettings().then((r) => live && setS(r), (e) => live && setError(errorText(e)));
    return () => { live = false; };
  }, []);

  const c = s?.content;
  return (
    <main className="flex flex-col gap-4 p-6">
      <PageHeader title="Ayarlar" description={screens.find((s) => s.id === "E10")!.description} />
      <p className="text-sm text-muted-foreground">
        Ayarlar bu ekrandan değiştirilmez. Her değişiklik yeni bir sürüm olarak uygulanır ve{" "}
        <a href="/audit-log" onClick={(e) => navigate(e, "/audit-log")} className="text-primary underline-offset-4 hover:underline">Denetim Logu</a>'na yazılır.
      </p>
      {error ? <ErrorAlert>{error}</ErrorAlert> : !s || !c ? (
        <div className="grid gap-4 xl:grid-cols-2"><CardSkeleton /><CardSkeleton /><CardSkeleton /><CardSkeleton /></div>
      ) : (
        <div className="grid gap-4 xl:grid-cols-2">
          <Dl id="ver-h" title="Sürüm" icon={Info} rows={[
            ["Sürüm", String(s.version), "tabular-nums"], ["Uygulanma", formatAuditTime(s.appliedAt), "tabular-nums"],
            ["Uygulayan", auditActorText(s.appliedBy)], ["Gerekçe", s.justification, "whitespace-normal"],
          ]} />
          <Dl id="co-h" title="Firma" icon={Building2} rows={[
            ["Firma adı", c.company.name],
            ["Yetkiler", c.company.authorizations.length
              ? <ul>{c.company.authorizations.map((a) => <li key={a}>{AUTHORIZATION_LABEL[a]}</li>)}</ul>
              : "Yetki tanımlı değil."],
          ]} />
          <Card role="region" aria-labelledby="tol-h" className="gap-3">
            <CardHead id="tol-h" title="Toleranslar" icon={SlidersHorizontal} />
            <Table>
              <TableHeader><TableRow><TableHead className="bg-muted/60 text-xs font-medium text-muted-foreground">Ayar</TableHead><TableHead className="bg-muted/60 text-xs font-medium text-muted-foreground text-right">Değer</TableHead></TableRow></TableHeader>
              <TableBody>
                {(Object.keys(TOLERANCE_LABEL) as (keyof Settings["tolerance"])[]).map((k) => (
                  <TableRow key={k}><TableCell>{TOLERANCE_LABEL[k].label}</TableCell><TableCell className="text-right tabular-nums">{toleranceText(k, c.tolerance[k])}</TableCell></TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
          <Card role="region" aria-labelledby="cam-h" className="min-w-0 gap-3">
            <CardHead id="cam-h" title="Kamera ↔ kapı eşlemesi" icon={Cctv} />
            <div className="max-h-72 overflow-auto">
              <Table containerClassName="overflow-visible">
                <TableHeader><TableRow><TableHead className={HEAD}>Kamera kodu</TableHead><TableHead className={HEAD}>Kapı</TableHead></TableRow></TableHeader>
                <TableBody>
                  {Object.entries(c.camera_gate).sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0)).map(([cam, gate]) => (
                    <TableRow key={cam}><TableCell className="font-mono">{cam}</TableCell><TableCell>{gate}</TableCell></TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </Card>
        </div>
      )}
    </main>
  );
}
